import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import {
  Box,
  Button,
  Divider,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import LinkIcon from '@mui/icons-material/Link';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import './RichTextEditor.css';
import { TEMPLATE_VARIABLE_GROUPS } from '../../constants/templateVariables';

const ToolbarButton = ({ title, onClick, active, disabled, children }) => (
  <Tooltip title={title}>
    <span>
      <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        color={active ? 'primary' : 'default'}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

const RichTextEditor = ({ value = '', onChange, minHeight = 300, showVariableMenu = true }) => {
  const [variableAnchor, setVariableAnchor] = React.useState(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false }),
      Image,
    ],
    content: value || '',
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || '';
    if (current !== next) {
      editor.commands.setContent(next, false);
    }
  }, [editor, value]);

  if (!editor) {
    return null;
  }

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const insertVariable = (variable) => {
    editor.chain().focus().insertContent(variable).run();
    setVariableAnchor(null);
  };

  return (
    <Box className="rich-text-editor">
      <Box className="rich-text-editor__toolbar">
        <ToolbarButton
          title="Undo"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
        >
          <UndoIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Redo"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
        >
          <RedoIcon fontSize="small" />
        </ToolbarButton>

        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

        <ToolbarButton
          title="Bold"
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
        >
          <FormatBoldIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Italic"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
        >
          <FormatItalicIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Underline"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}
        >
          <FormatUnderlinedIcon fontSize="small" />
        </ToolbarButton>

        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

        <ToolbarButton
          title="Align left"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          active={editor.isActive({ textAlign: 'left' })}
        >
          <FormatAlignLeftIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Align center"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          active={editor.isActive({ textAlign: 'center' })}
        >
          <FormatAlignCenterIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Align right"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          active={editor.isActive({ textAlign: 'right' })}
        >
          <FormatAlignRightIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Justify"
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          active={editor.isActive({ textAlign: 'justify' })}
        >
          <FormatAlignJustifyIcon fontSize="small" />
        </ToolbarButton>

        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

        <ToolbarButton
          title="Bullet list"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
        >
          <FormatListBulletedIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton
          title="Numbered list"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
        >
          <FormatListNumberedIcon fontSize="small" />
        </ToolbarButton>

        <ToolbarButton title="Link" onClick={setLink} active={editor.isActive('link')}>
          <LinkIcon fontSize="small" />
        </ToolbarButton>

        <ToolbarButton
          title="Clear formatting"
          onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
        >
          <FormatClearIcon fontSize="small" />
        </ToolbarButton>

        {showVariableMenu && (
          <>
            <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => setVariableAnchor(e.currentTarget)}
              aria-haspopup="true"
              aria-expanded={Boolean(variableAnchor)}
              sx={{ textTransform: 'none', ml: 0.5 }}
            >
              Insert variable
            </Button>
            <Menu
              anchorEl={variableAnchor}
              open={Boolean(variableAnchor)}
              onClose={() => setVariableAnchor(null)}
            >
              {TEMPLATE_VARIABLE_GROUPS.map((group) => [
                <ListSubheader key={`${group.title}-header`}>{group.title}</ListSubheader>,
                ...group.variables.map((item) => (
                  <MenuItem key={item.value} onClick={() => insertVariable(item.value)}>
                    {item.label} ({item.value})
                  </MenuItem>
                )),
              ])}
            </Menu>
          </>
        )}
      </Box>

      <Box
        className="rich-text-editor__content"
        sx={{ minHeight }}
        onClick={() => editor.chain().focus().run()}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

export default RichTextEditor;
